/**
 * Site Overlay - Portal-based overlay/modal system with stacking support
 */
class SiteOverlay extends HTMLElement {
  constructor() {
    super();
    this.overlayStack = []; // Array of { id, element, trigger } objects
    this.templates = new Map(); // Map of overlay ID to template element
    this.hashChangeHandler = this.handleHashChange.bind(this);
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    document.addEventListener("keydown", this.handleKeydown.bind(this));
    document.addEventListener("click", this.handleTriggerClick.bind(this));
    this.checkInitialHash();
    window.addEventListener("hashchange", this.hashChangeHandler);
  }

  disconnectedCallback() {
    window.removeEventListener("hashchange", this.hashChangeHandler);
  }

  register({ id, autoOpen }) {
    this.templates.set(id, true);

    if (autoOpen) {
      requestAnimationFrame(() => this.open(id, true));
    }
  }

  getTemplate(id) {
    return document.querySelector(`template[data-overlay-template="${id}"]`);
  }

  checkInitialHash() {
    const hash = window.location.hash;
    if (hash.startsWith("#overlay-")) {
      // Support deep linking: open overlay from URL hash
      const id = hash.replace("#overlay-", "");

      // Immediately replace the hash (without adding to history)
      // This allows deep linking while preventing back button issues
      history.replaceState("", document.title, window.location.pathname);

      // Wait for overlay to be registered, then open it
      this.waitForOverlayAndOpen(id, 10, 100);
    }
  }

  waitForOverlayAndOpen(id, maxRetries, delay) {
    const attempt = (retriesLeft) => {
      if (this.overlayStack.find((o) => o.id === id)) return;

      if (this.templates.has(id) && this.getTemplate(id)) {
        this.open(id, true);
        return;
      }

      if (retriesLeft > 0) {
        setTimeout(() => attempt(retriesLeft - 1), delay);
      } else {
        console.warn(`Overlay "${id}" not found after retries`);
      }
    };

    setTimeout(() => attempt(maxRetries), delay);
  }

  handleHashChange() {
    const hash = window.location.hash;

    // If hash is removed or not an overlay hash, close all overlays
    if (!hash || !hash.startsWith("#overlay-")) {
      this.closeAll();
      return;
    }

    // Don't reopen overlays from browser navigation (back/forward)
    // Only handle hashes for initial page load via checkInitialHash()
    // This prevents back button from reopening closed overlays
    const id = hash.replace("#overlay-", "");
    const existingIndex = this.overlayStack.findIndex((o) => o.id === id);

    if (existingIndex === -1) {
      // Overlay not open and hash present - clear the hash without opening
      history.replaceState("", document.title, window.location.pathname);
    }
  }

  open(id, skipHistory = false) {
    if (!this.templates.has(id)) return;

    const template = this.getTemplate(id);
    if (!template) return;

    if (this.overlayStack.find((o) => o.id === id)) return;

    const clone = template.content.cloneNode(true);

    const overlayWrapper = document.createElement("div");
    overlayWrapper.className = "overlay";
    overlayWrapper.dataset.overlayId = id;
    overlayWrapper.appendChild(clone);

    this.appendChild(overlayWrapper);

    // Force layout calculation to enable CSS transition
    const contentElement = overlayWrapper.querySelector(".overlay__content");
    if (contentElement) {
      contentElement.offsetHeight;
    }

    const trigger = document.activeElement;

    this.overlayStack.push({
      id,
      element: overlayWrapper,
      trigger,
    });

    if (this.overlayStack.length > 1) {
      const previousOverlay = this.overlayStack[this.overlayStack.length - 2];
      this.setInactive(previousOverlay.element);
    }

    const closeButton = overlayWrapper.querySelector(".overlay__close");
    if (closeButton) {
      closeButton.addEventListener("click", () => this.close());
    }

    const backdrop = overlayWrapper.querySelector(".overlay__backdrop");
    if (backdrop) {
      backdrop.addEventListener("click", () => this.close());
    }

    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add("overlay-open");
    this.classList.add("has-overlays");

    requestAnimationFrame(() => {
      overlayWrapper.classList.add("active");
      overlayWrapper.dispatchEvent(new CustomEvent('overlay:opened', { bubbles: true }));
    });

    // Update URL to show overlay state, but don't add to history
    // Using replaceState prevents back button from reopening overlay
    if (!skipHistory) {
      const newUrl = `${window.location.pathname}${window.location.search}#overlay-${id}`;
      history.replaceState("", document.title, newUrl);
    }
  }

  close(id = null) {
    if (this.overlayStack.length === 0) return;

    if (!id) {
      const topOverlay = this.overlayStack[this.overlayStack.length - 1];
      id = topOverlay.id;
    }

    const index = this.overlayStack.findIndex((o) => o.id === id);
    if (index === -1) return;

    const overlay = this.overlayStack[index];

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus();
    }

    overlay.element.classList.remove("active");
    overlay.element.classList.add("closing");

    this.overlayStack.splice(index, 1);

    setTimeout(() => {
      if (overlay.element.parentElement) overlay.element.remove();
    }, 800);

    if (this.overlayStack.length === 0) {
      // Remove hash when closing last overlay
      const newUrl = `${window.location.pathname}${window.location.search}`;
      history.replaceState("", document.title, newUrl);
      this.restorePageState(overlay.trigger);
    } else {
      const newTop = this.overlayStack[this.overlayStack.length - 1];
      // Update hash to show the new top overlay
      const newUrl = `${window.location.pathname}${window.location.search}#overlay-${newTop.id}`;
      history.replaceState("", document.title, newUrl);
      this.setActive(newTop.element);
    }
  }

  closeAll() {
    let firstTrigger = null;
    while (this.overlayStack.length > 0) {
      const topOverlay = this.overlayStack[this.overlayStack.length - 1];

      if (!firstTrigger && topOverlay.trigger) {
        firstTrigger = topOverlay.trigger;
      }

      if (typeof removeTrapFocus === "function") {
        removeTrapFocus();
      }

      topOverlay.element.classList.remove("active");
      topOverlay.element.classList.add("closing");

      const element = topOverlay.element;
      setTimeout(() => {
        if (element.parentElement) element.remove();
      }, 800);

      this.overlayStack.pop();
    }

    // Clear hash immediately to prevent re-triggering
    if (window.location.hash.startsWith("#overlay-")) {
      history.replaceState("", document.title, window.location.pathname);
    }

    setTimeout(() => {
      this.restorePageState(firstTrigger);
    }, 800);
  }

  restorePageState(focusTarget = null) {
    const scrollY = document.body.style.top;
    document.body.classList.remove("overlay-open");
    document.body.style.top = '';
    this.classList.remove("has-overlays");
    window.scrollTo(0, parseInt(scrollY || '0') * -1);

    if (window.location.hash.startsWith("#overlay-")) {
      history.replaceState("", document.title, window.location.pathname);
    }

    if (focusTarget && typeof focusTarget.focus === "function") {
      focusTarget.focus();
    }
  }

  setInactive(overlayElement) {
    overlayElement.classList.add("inactive");
    overlayElement.classList.remove("active");

    if (typeof removeTrapFocus === "function") removeTrapFocus();
  }

  setActive(overlayElement) {
    overlayElement.classList.remove("inactive");
    overlayElement.classList.add("active");

    const content = overlayElement.querySelector(".overlay__content");
    if (content && typeof trapFocus === "function") {
      requestAnimationFrame(() => {
        trapFocus(content, content);
      });
    }
  }

  handleKeydown(e) {
    if (e.key === "Escape" && this.overlayStack.length > 0) {
      e.preventDefault();
      this.close();
    }
  }

  handleTriggerClick(e) {
    const trigger = e.target.closest("[data-overlay-trigger]");
    if (!trigger) return;

    const overlayId = trigger.dataset.overlayTrigger;
    if (!overlayId) return;

    e.preventDefault();
    this.open(overlayId);
  }
}

customElements.define("site-overlay", SiteOverlay);

window.siteOverlayQueue = window.siteOverlayQueue || [];

window.siteOverlay = {
  register: function (config) {
    window.siteOverlayQueue.push(config);
  },
  open: function () {},
};

// Create singleton on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSiteOverlay);
} else {
  initSiteOverlay();
}

function initSiteOverlay() {
  if (!customElements.get("site-overlay")) return;

  let overlay = document.querySelector("site-overlay");
  if (!overlay) {
    overlay = document.createElement("site-overlay");
    document.body.appendChild(overlay);
  }

  window.siteOverlay = overlay;

  if (window.siteOverlayQueue && window.siteOverlayQueue.length > 0) {
    window.siteOverlayQueue.forEach((config) => {
      overlay.register(config);
    });
    window.siteOverlayQueue = [];
  }
}
